import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Trash2 from "@NA/Icons/Trash2";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import TextField from "@insite/mobius/TextField";
import { css } from "styled-components";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addOrRemoveCart } from "@NA/Utils/GtmUtility";
import { Session } from "@insite/client-framework/Services/SessionService";
import { removeProductFromCart } from "@NA/Overrides/Common/Utilities/CustomTracking";
import useDebounce from "@NA/Hooks/useDebounce";
import addToCart from "@NA/Handlers/ADIAddToCart";
import ADIMinimumQtyModal from "@NA/Components/ADIMinimumQtyModal";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { checkAllowedQuantity } from "@NA/Utils/Utility";

interface OwnProps {
    product: CartLineModel | null | undefined;
    orderLine: OrderLineModel;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        addingProductToCart: state.context.isUpdatingCart,
        addToCartLoading: state.context.isUpdatingCart,
        session: state.context.session,
        updateCartLoading: state.pages.cart.isUpdatingCartLine,
        removeCartLineLoading: state.pages.cart.isRemovingCartLine,
        isCartLoading: getCurrentCartState(state).isLoading || state.ADIGlobalData.ADIUpdateCartLoading.loading,
    };
};

export interface AdiBuyItAgainButtonStyles {
    addToCartButton?: ButtonPresentationProps;
    container?: InjectableCss;
    iconStyles?: InjectableCss;
    iconStylesDecrease?: InjectableCss;
    field?: InjectableCss;
    loading?: InjectableCss;
    loadingUpdate?: InjectableCss;
}

export const adiBuyItAgainButtonStyles: AdiBuyItAgainButtonStyles = {
    iconStylesDecrease: {
        css: css`
            border-top-left-radius: 3.5px;
            border-bottom-left-radius: 3.5px;
            border: 1px solid ${getColor("text.link")};
            display: flex;
            align-items: center;
            justify-content: center;
            width: 50px;
            height: auto;
            min-width: 50px;
            cursor: pointer;
            transition: all 0.2s ease;
            :hover {
                background-color: ${getColor("primary")};
                svg {
                    stroke: ${getColor("common.background")};
                }
            }
            @media only screen and (max-width: 576px) {
                border: none;
            }
        `,
    },
    iconStyles: {
        css: css`
            border-top-right-radius: 3.5px;
            border-bottom-right-radius: 3.5px;
            border: 1px solid ${getColor("text.link")};
            display: flex;
            align-items: center;
            justify-content: center;
            width: 50px;
            height: auto;
            min-width: 50px;
            cursor: pointer;
            transition: all 0.2s ease;
            :hover {
                background-color: ${getColor("primary")};
                svg {
                    stroke: ${getColor("common.background")};
                }
            }
            @media only screen and (max-width: 576px) {
                border: none;
            }
        `,
    },
    addToCartButton: {
        variant: "secondary",
        css: css`
            max-width: 210px;
            width: 100%;
            word-break: break-word;
            display: flex;
            align-items: center;
            font-size: 16px;
            font-weight: bold;
            justify-content: center;
            border-radius: 4px;
            border: solid 1px ${getColor("primary")};
            background: ${getColor("common.background")};
            color: ${getColor("primary")};
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${getColor("primary")} !important;
                color: ${getColor("common.background")} !important;
                border: none !important;
            }
            @media only screen and (max-width: 576px) {
                line-height: normal;
                border: 0;
                box-shadow: none;
                padding: 21px 10px !important;
                max-width: 100%;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            max-width: 210px;
            width: 100%;
            height: 40px;
            flex-direction: row;
            border-radius: 4px;
            @media only screen and (max-width: 576px) {
                min-height: 55px;
                max-width: 100%;
            }
        `,
    },
    field: {
        css: css`
            width: 100%;
            [class^="FormFieldStyle-"] {
                height: 100%;
                width: 100%;
                display: block;
            }
            [class^="FormInputWrapper-"] {
                height: 100%;
                margin-top: 0px !important;
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
                input {
                    border-top: 1px solid ${getColor("text.link")} !important;
                    border-bottom: 1px solid ${getColor("text.link")} !important;
                    border-left: 0 !important;
                    border-right: 0 !important;
                    width: 100%;
                    height: 100%;
                    font-size: 16px;
                    font-weight: bold;
                    color: ${getColor("text.main")};
                    text-align: center;
                    margin: 0 0 1rem 0;
                    padding: 0.5rem;
                    font-size: 14px;
                    line-height: 1;
                    border-radius: 0 !important;
                    &:focus {
                        border-top: 1px solid ${getColor("text.link")} !important;
                        border-bottom: 1px solid ${getColor("text.link")} !important;
                        border-left: 0 !important;
                        border-right: 0 !important;
                        outline: none !important;
                        box-shadow: none !important;
                    }
                    @media only screen and (max-width: 576px) {
                        border: none !important;
                    }
                }
            }
        `,
    },
    loading: {
        css: css`
            width: 35%;
            display: flex;
            justify-content: center;
            position: relative;
            align-items: center;
            min-height: 40px;
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: none;
            img {
                width: 40px;
                position: absolute;
            }
            @media only screen and (max-width: 576px) {
                min-height: 24px;
            }
        `,
    },
    loadingUpdate: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            position: relative;
            align-items: center;
            min-height: 40px;
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: none;
            border-top: 1px solid ${getColor("text.link")} !important;
            border-bottom: 1px solid ${getColor("text.link")} !important;
            img {
                width: 40px;
                position: absolute;
            }
        `,
    },
};

const styles = adiBuyItAgainButtonStyles;

const ADIBuyItAgainButton = ({
    product,
    orderLine,
    addToCartLoading,
    session,
    updateCartLoading,
    removeCartLineLoading,
    isCartLoading,
}: Props) => {
    const [addToCartLoadingUpdated, setAddToCartLoadingUpdated] = useState(false);
    const [productQty, setProductQty] = useState("0");
    const [operationClicked, setOperationClicked] = useState("");
    const [addClicks, setAddClicks] = useState(0);
    const [minusClicks, setMinusClicks] = useState(0);
    const latestAddClicks = React.useRef(addClicks);
    const latestMinusClicks = React.useRef(minusClicks);
    const dispatch: any = useDispatch();
    const [isMinimumQtyOpen, setIsMinimumQtyOpen] = useState(false);
    const [updateQuantityLoader, setUpdateQuantityLoader] = React.useState(false);
    const [selectedProduct, setSelectedProduct] = React.useState<string>("");

    const currentProductSelected = selectedProduct === product?.id;

    useEffect(() => {
        if (!product) {
            return;
        }
        if (product && product.qtyOrdered === Number(productQty)) {
            return;
        }
        if (product && product.qtyOrdered) {
            setProductQty(product.qtyOrdered.toString());
        }
    }, [product]);

    const closeMinimumQtyCard = () => setIsMinimumQtyOpen(false);

    const debouncedIncreaseQuantity = useDebounce(() => {
        if (!addToCartLoading && product) {
            sessionStorage.setItem("item_list_name", "Order Details");
            setOperationClicked("1");
            setAddClicks(0);
            dispatch(
                addToCart({
                    productId: orderLine.productId,
                    qtyOrdered: latestAddClicks.current,
                    unitOfMeasure: product.unitOfMeasure,
                    onComplete() {
                        setOperationClicked("");
                        setAddToCartLoadingUpdated(false);
                        latestAddClicks.current = 0;
                    },
                }),
            );
        }
    }, 500);

    const debouncedDecreaseQuantity = useDebounce(() => {
        if (!addToCartLoading && product) {
            setMinusClicks(0);
            dispatch(
                updateCartLine({
                    cartLine: {
                        ...(product as CartLineModel),
                        qtyOrdered: Number(Number(product.qtyOrdered) - latestMinusClicks.current),
                    },
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setOperationClicked("");
                        latestMinusClicks.current = 0;
                    },
                }),
            );
        }
    }, 500);

    const addToCartClickHandler = async () => {
        if (!addToCartLoading && !updateQuantityLoader) {
            setAddToCartLoadingUpdated(true);
            setOperationClicked("add");
            dispatch(
                addToCart({
                    productId: orderLine.productId,
                    qtyOrdered: orderLine.qtyOrdered,
                    unitOfMeasure: orderLine.unitOfMeasure || "EA",
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setOperationClicked("");
                    },
                }),
            );
        }
    };



    const increaseQuantity = async () => {
        if (!addToCartLoading && product && !isCartLoading) {
            setOperationClicked("1");

            setAddClicks(prevClicks => prevClicks + Number(product.properties.minimuM_QTY));
            latestAddClicks.current = addClicks + Number(product.properties.minimuM_QTY);
            if (!checkAllowedQuantity(Number(productQty), Number(product.properties.minimuM_QTY))) {
                setIsMinimumQtyOpen(true);
                setOperationClicked("");
                return;
            }
            setProductQty(prev => String(Number(prev) + parseInt(product.properties.minimuM_QTY)));
            sessionStorage.setItem("item_list_name", "Product List Page");
            setSelectedProduct(product.id);
            debouncedIncreaseQuantity(product.id);
        }
    };

    const decreaseQuantity = () => {
        if (!product && !isCartLoading) return;
        if (!addToCartLoading && product && !isCartLoading) {
            setOperationClicked("0");

            if (!checkAllowedQuantity(Number(productQty), Number(product.properties.minimuM_QTY))) {
                setIsMinimumQtyOpen(true);
                setOperationClicked("");
                return;
            }

            if (productQty === product.properties.minimuM_QTY) {
                setSelectedProduct(product.id);
                setAddToCartLoadingUpdated(true);
                dispatch(
                    removeCartLine({
                        cartLineId: product.id,
                        onComplete() {
                            setAddToCartLoadingUpdated(false);
                            setOperationClicked("");
                        },
                    }),
                );
                if (product) {
                    sessionStorage.setItem("item_list_name", "Order Details");
                    removeProductFromCart(product, "Buy again from order details page", session);
                }
            } else {
                setSelectedProduct(product.id);
                setAddToCartLoadingUpdated(true);
                setMinusClicks(prevClicks => prevClicks + Number(product.properties.minimuM_QTY));
                latestMinusClicks.current = minusClicks + Number(product.properties.minimuM_QTY);
                setProductQty(prev => String(Number(prev) - parseInt(product.properties.minimuM_QTY)));
                debouncedDecreaseQuantity(product.id);
                if (product) {
                    sessionStorage.setItem("item_list_name", "Order Details");
                    const cart: CartLineModel = product;
                    removeProductFromCart(cart, "Order Details", session);
                }
            }
        }
    };

    const updateQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (!product) { return }

        if (value === "" || value === product?.qtyOrdered!.toString()) {
            setAddToCartLoadingUpdated(false);
            setProductQty(product?.qtyOrdered!.toString());
        }

        if (!value && value !== "0") {
            return;
        }

        setAddToCartLoadingUpdated(true);
        setOperationClicked("update");

        if (value === "0") {
            setUpdateQuantityLoader(true);
            setSelectedProduct(product.id);
            dispatch(
                removeCartLine({
                    cartLineId: product.id,
                    onComplete() {
                        setUpdateQuantityLoader(false);
                        setAddToCartLoadingUpdated(false);
                    },
                }),
            );
            sessionStorage.setItem("item_list_name", "Order Details");
            addOrRemoveCart(product, 0, session);
            return;
        }

        if (Boolean(value) && Number(value) && product.qtyOrdered !== Number(value)) {
            if (!checkAllowedQuantity(Number(value), Number(product.properties.minimuM_QTY))) {
                if (product && product.qtyOrdered) {
                    setProductQty(product.qtyOrdered?.toString());
                }
                setIsMinimumQtyOpen(true);
                return;
            }
            setUpdateQuantityLoader(true);
            setSelectedProduct(product.id);
            dispatch(
                updateCartLine({
                    cartLine: {
                        ...product,
                        qtyOrdered: Number(event.target.value),
                    },
                    onComplete() {
                        setUpdateQuantityLoader(false);
                        setAddToCartLoadingUpdated(false);
                    },
                }),
            );
            sessionStorage.setItem("item_list_name", "Order Details");
            const qty = Number(event.target.value);
            addOrRemoveCart(product, qty, session);
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);
    };

    const addLoader = (type = "") => {
        return (
            <StyledWrapper {...(type === "" ? styles.loading : styles.loadingUpdate)}>
                <img alt="" src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif" />
            </StyledWrapper>
        );
    };

    return (
        <>
            {product && (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper
                        onClick={() => {
                            if (!addToCartLoading || !updateCartLoading || !isCartLoading || !addToCartLoadingUpdated) {
                                decreaseQuantity();
                            }
                        }}
                        {...styles.iconStylesDecrease}
                    >
                        {(updateCartLoading || Object.keys(removeCartLineLoading).length || isCartLoading) &&
                            operationClicked === "0" &&
                            currentProductSelected ? (
                            addLoader()
                        ) : productQty === product.properties.minimuM_QTY || productQty === "0" ? (
                            <Trash2 />
                        ) : (
                            <Minus />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.field}>
                        {updateQuantityLoader ? (
                            addLoader("input")
                        ) : (
                            <TextField
                                value={productQty}
                                type="text"
                                min={0}
                                max={999999}
                                maxLength={5}
                                disabled={addToCartLoadingUpdated || addToCartLoading || isCartLoading}
                                data-test-selector="product_qtyOrdered"
                                onBlur={updateQuantity}
                                onChange={qtyChangeHandler}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper
                        onClick={() => {
                            if (!addToCartLoading || !updateCartLoading || !isCartLoading || !addToCartLoadingUpdated) {
                                increaseQuantity();
                            }
                        }}
                        {...styles.iconStyles}
                    >
                        {(addToCartLoading || isCartLoading) && operationClicked === "1" && currentProductSelected ? (
                            addLoader()
                        ) : (
                            <Plus />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {!product && (
                <Button
                    {...styles.addToCartButton}
                    onClick={addToCartClickHandler}
                    disabled={addToCartLoadingUpdated || updateCartLoading || isCartLoading}
                    variant="secondary"
                >
                    {addToCartLoadingUpdated ? addLoader() : translate("Buy it Again")}
                </Button>
            )}

            <ADIMinimumQtyModal
                headline="Add to Cart Message"
                isOpen={isMinimumQtyOpen}
                errorStyle={false}
                handleClose={closeMinimumQtyCard}
            >
                <>
                    {translate("Must be purchased in quantities of")} {product && product.properties.minimuM_QTY}
                </>
            </ADIMinimumQtyModal>
        </>
    );
};
export default connect(mapStateToProps)(withProductContext(ADIBuyItAgainButton));
